import { ONE_MINUTE_IN_MS, ONE_HOUR_IN_MS, ONE_SECOND_IN_MS } from "@cede/utils";

// Shared color palette for PnL chart and stats card
export const CURRENT_USER_COLOR = "#3B82F6"; // Electric Blue
export const RANK_COLORS = ["#EAB308", "#94A3B8", "#A16207"] as const; // Gold, Silver, Bronze
export const DEFAULT_RANK_COLOR = "#64748B"; // Slate-500, for ranks beyond top 3
export const OTHER_COLORS = ["#64748B", "#7C8FA8", "#5B7A99", "#8B9DB5", "#6B839E", "#4E6A85"] as const;
export const OTHER_OPACITY = 0.6;

// Chart layout constants
export const CHART_MARGIN = { left: 16, right: 16, top: 32, bottom: 8 } as const;
export const Y_AXIS_WIDTH = 64;
export const Y_AXIS_TARGET_TICKS = 6;
export const X_AXIS_TARGET_TICKS = 6;

// Chart animation / layout constants
export const ENTRANCE_ANIMATION_MS = 900;
export const CHART_Y_PADDING_RATIO = 0.1;
export const CHART_RIGHT_MARGIN_RATIO = 0.2;
export const CHART_ALL_LEFT_MARGIN_RATIO = 0.03;
export const CHART_ALL_LEFT_MARGIN_MIN_MS = 60_000;
export const PNL_PRICE_TICK_INTERVAL_MS = 5 * ONE_SECOND_IN_MS;
export const DEFAULT_X_TICK_STEP_MS = 3_600_000;

// Tooltip label layout
export const TOOLTIP_LABEL_HEIGHT = 18;
export const TOOLTIP_MIN_SPACING = 4;
export const TOOLTIP_HORIZONTAL_OFFSET = 6;
export const HOVERED_USER_LABEL_X_OFFSET = 14;
export const HOVERED_USER_LABEL_Y_OFFSET = -10;

// Pick a "nice" round tick interval (1, 2, 5, 10, 20, 50, …) based on the data range
export function niceTickInterval(range: number, targetTicks = Y_AXIS_TARGET_TICKS): number {
  if (range <= 0) return 1;
  const roughStep = range / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;
  const niceNorm = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNorm * magnitude;
}

// Nice time intervals in milliseconds for X axis ticks.
// Capped at 12h since the chart only surfaces a rolling 12h window.
const TIME_STEPS_MS = [
  1 * ONE_MINUTE_IN_MS, // 1 min
  2 * ONE_MINUTE_IN_MS, // 2 min
  5 * ONE_MINUTE_IN_MS, // 5 min
  10 * ONE_MINUTE_IN_MS, // 10 min
  15 * ONE_MINUTE_IN_MS, // 15 min
  30 * ONE_MINUTE_IN_MS, // 30 min
  1 * ONE_HOUR_IN_MS, // 1 hour
  2 * ONE_HOUR_IN_MS, // 2 hours
  4 * ONE_HOUR_IN_MS, // 4 hours
  6 * ONE_HOUR_IN_MS, // 6 hours
  12 * ONE_HOUR_IN_MS, // 12 hours
] as const;

/**
 * Pick a "nice" time interval from the predefined list that produces
 * close to `targetTicks` ticks for the given time range.
 */
export function niceTimeInterval(rangeMs: number, targetTicks = X_AXIS_TARGET_TICKS): number {
  if (rangeMs <= 0) return TIME_STEPS_MS[0];
  const roughStep = rangeMs / targetTicks;
  // Find the step that is closest to roughStep
  let best = TIME_STEPS_MS[0];
  let bestDiff = Math.abs(roughStep - best);
  for (const step of TIME_STEPS_MS) {
    const diff = Math.abs(roughStep - step);
    if (diff < bestDiff) {
      best = step;
      bestDiff = diff;
    }
  }
  return best;
}

// Time range options for x-axis scale selection.
// Capped at 12h to match the backend price-event window (see
// apps/api/src/modules/pnl-snapshot/presentation/dtos/get-market-price-snapshots.dto.ts).
export const TIME_RANGE_OPTIONS = [
  {
    key: "1h",
    label: "1H",
    durationMs: 1 * ONE_HOUR_IN_MS,
    tickInterval: 5 * ONE_MINUTE_IN_MS,
  },
  {
    key: "6h",
    label: "6H",
    durationMs: 6 * ONE_HOUR_IN_MS,
    tickInterval: 1 * ONE_HOUR_IN_MS,
  },
  {
    key: "12h",
    label: "12H",
    durationMs: 12 * ONE_HOUR_IN_MS,
    tickInterval: 2 * ONE_HOUR_IN_MS,
  },
] as const;

export type TimeRangeKey = (typeof TIME_RANGE_OPTIONS)[number]["key"];

export function formatUsdNoCurrency(value: number): string {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const pnlColor = (value: number) => (value >= 0 ? "text-cede-pnl-positive" : "text-cede-error");

/** How far back to look for rank evolution (ms) */
export const RANK_EVOLUTION_WINDOW_MS = 30 * ONE_MINUTE_IN_MS; // 30 minutes

// Leaderboard responsive width breakpoints (px)
/** Show volume column */
export const BP_VOLUME = 400;
/** Show balance column / compact mode threshold */
export const BP_COMPACT = 550;
/** Medium row sizing */
export const BP_MEDIUM = 700;
/** Large row sizing */
export const BP_LARGE = 900;

export function getRankColor(rank: number | null, isCurrentUser: boolean): string {
  if (isCurrentUser) return CURRENT_USER_COLOR;
  if (rank !== null && rank <= 3) return RANK_COLORS[rank - 1] ?? DEFAULT_RANK_COLOR;
  return DEFAULT_RANK_COLOR;
}
